import JSZip from 'jszip';
import { captureDeviceLogs, loadDeviceConfigSnapshot, loadDeviceLogCaptures, downloadDeviceLog } from './siteControlApi';

export const LOG_TYPES = [
  { id: 'android', label: 'Android logs', detail: 'Current capture: app, system and crash messages retained by Android.' },
  { id: 'suitestream', label: 'Suitestream app logs', detail: 'A separate file of messages matching known Suitestream app tags, including startup, relay connections and forwarded WebView errors. Extracted from Android logs; Android logs provides wider context.' },
  { id: 'events', label: 'Android events', detail: 'Structured system and activity events.', future: true },
  { id: 'radio', label: 'Radio logs', detail: 'Telephony and radio service messages where available.', future: true },
  { id: 'kernel', label: 'Kernel logs', detail: 'Low-level device and driver messages where permitted.', future: true },
  { id: 'diagnostics', label: 'Device diagnostics', detail: 'Current device, memory, network and service state; not historical logs.', future: true },
];
export const LOG_LEVELS = [
  ['V', 'Verbose'], ['D', 'Debug'], ['I', 'Info'], ['W', 'Warnings'], ['E', 'Errors'],
  ['F', 'Fatal'], ['other', 'Unclassified / other'],
];
export const DEFAULT_LEVELS = LOG_LEVELS.map(([id]) => id);
const active = (capture) => ['queued', 'running'].includes(capture.status);

// Match the tags in android-app's native logging. Legacy tag strings remain
// necessary for installed builds. This is a focused extract, not process attribution.
const SUITESTREAM_TAGS = new Set(['SoundScreenApp', 'SoundScreenBoot', 'CommandService', 'WebAppInterface',
  'BootReceiver', 'NetworkScanner', 'SystemOverlayService', 'AndroidTvRemoteProxy', 'DeviceIdentityRecovery',
  'LgWebOsClient', 'AppConfigStore', 'RemoteControlService']);

export function filterSuitestreamLogs(text) {
  let include = false;
  return text.split(/(?<=\n)/).filter((line) => {
    if (/^\s*\d{9,12}(?:\.\d+)?\s/.test(line)) {
      const match = line.match(/^\s*\d{9,12}(?:\.\d+)?\s+\d+\s+\d+\s+[VDIWEFA]\s+([^:]+):/);
      include = Boolean(match && SUITESTREAM_TAGS.has(match[1].trim()));
    } else if (line.startsWith('--------- ')) include = false;
    return include;
  }).join('');
}

// Keep continuation lines with their preceding epoch-formatted log entry.
export function filterLogLevels(text, levels) {
  if (DEFAULT_LEVELS.every((level) => levels.includes(level))) return text;
  let level = 'other';
  return text.split(/(?<=\n)/).filter((line) => {
    const match = line.match(/^\s*\d{9,12}(?:\.\d+)?\s+\d+\s+\d+\s+([VDIWEFA])\s/);
    if (match) level = match[1] === 'A' ? 'F' : match[1];
    return levels.includes(level);
  }).join('');
}

export function saveLogDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = filename;
  document.body.appendChild(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// One active target at a time lets a single relay serve the entire selection.
// An uncertain admission stops the batch; its request key is retained for a safe retry.
export async function collectDeviceLogs({ siteId, targets, minutes, requests, newId,
  isActive, shouldStop, wait, onCapture, onProgress }) {
  const items = targets.map((target) => ({ deviceId: target.deviceId, name: target.name, status: 'waiting' }));
  const report = () => { if (isActive()) onProgress(items.map((item) => ({ ...item }))); };
  report();
  for (const item of items) {
    if (!isActive()) return null;
    if (shouldStop()) break;
    item.status = 'preparing'; report();
    let capture;
    let previous = [...requests.entries()].find(([, request]) => request.siteId === siteId && request.deviceId === item.deviceId);
    if (previous) {
      item.requestKey = previous[1].id;
      try {
        const result = await loadDeviceLogCaptures(siteId);
        capture = result?.success && result.captures?.find((entry) => entry.id === item.requestKey);
      } catch (_) { /* Reconcile before considering another admission. */ }
      if (!isActive()) return null;
      if (capture) {
        requests.delete(previous[0]);
        previous = null;
        if (capture.minutes !== minutes) {
          onCapture(capture);
          if (active(capture)) {
            item.capture = capture; item.status = 'previous_capture_running'; report(); break;
          }
          capture = null;
        }
      }
    }
    if (!capture) {
      let fresh;
      try { fresh = await loadDeviceConfigSnapshot(siteId); }
      catch (_) { item.status = previous ? 'admission_uncertain' : 'readiness_unavailable'; report(); if (previous) break; continue; }
      if (!isActive()) return null;
      const target = fresh?.targets?.find((entry) => entry.deviceId === item.deviceId);
      if (!target?.logCaptureAvailable || !fresh.diagnosticsAvailable || !(fresh.expiresAtEpochMs > Date.now())) {
        item.status = previous ? 'admission_uncertain' : 'unavailable'; report(); if (previous) break; continue;
      }
      const payload = { minutes, expected: { authMode: target.authMode,
        identityGeneration: target.identityGeneration, endpointRevision: target.endpointRevision } };
      const signature = JSON.stringify([siteId, item.deviceId, payload]);
      if (previous && previous[0] !== signature) {
        item.status = 'admission_uncertain'; report(); break;
      }
      if (!requests.has(signature)) requests.set(signature, { id: newId(), siteId, deviceId: item.deviceId });
      const requestKey = requests.get(signature).id;
      item.requestKey = requestKey;
      try {
        const result = await captureDeviceLogs(siteId, item.deviceId, { ...payload, requestKey });
        if (!result?.success || !result.capture || result.capture.id !== requestKey) throw new Error('invalid_admission');
        capture = result.capture;
        requests.delete(signature);
      } catch (_) {
        item.status = 'admission_uncertain'; report(); break;
      }
    }
    if (!isActive()) return null;
    item.capture = capture; item.status = capture.status; onCapture(capture); report();
    // Bound the browser wait even if a response omits the server deadline.
    const deadline = Math.min(capture.deadlineEpochMs || Infinity, Date.now() + 120000);
    while (active(capture)) {
      if (Date.now() >= deadline) { item.status = 'still_running'; break; }
      await wait();
      if (!isActive()) return null;
      try {
        const result = await loadDeviceLogCaptures(siteId);
        if (!isActive()) return null;
        const latest = result?.success && result.captures?.find((entry) => entry.id === capture.id);
        if (!latest) { item.status = 'status_unavailable'; break; }
        capture = latest; item.capture = capture; item.status = capture.status;
        onCapture(capture); report();
      } catch (_) { item.status = 'status_unavailable'; break; }
    }
    report();
    // Do not admit another target while the shared relay may still be occupied.
    if (active(capture)) break;
  }
  for (const item of items) if (item.status === 'waiting') item.status = 'not_started';
  report();
  return items;
}

export async function buildDeviceLogPackage({ siteId, minutes, levels, items, logTypes = ['android'], isActive = () => true }) {
  if (!logTypes.length || logTypes.some((type) => !['android', 'suitestream'].includes(type))) throw new Error('unsupported_log_types');
  const zip = new JSZip();
  const results = [];
  let totalBytes = 0;
  for (const item of items) {
    if (!isActive()) return null;
    const result = { deviceId: item.deviceId, name: item.name, status: item.status,
      captureId: item.capture?.id || item.requestKey || null };
    const capture = item.capture;
    if (capture) {
      result.capture = Object.fromEntries(['minutes', 'createdAtEpochMs', 'targetEpochSeconds', 'relayDeviceId',
        'sizeBytes', 'sha256', 'scanTruncated', 'tailTruncated', 'sourceLineLimitReached', 'oversizedLinesDropped']
        .filter((key) => capture[key] !== undefined).map((key) => [key, capture[key]]));
    }
    if (item.status === 'succeeded' && capture) {
      try {
        const blob = await downloadDeviceLog(siteId, capture.id);
        if (!isActive()) return null;
        // Cap both each response and the entire archive's uncompressed log data.
        if (blob.size !== capture.sizeBytes || blob.size > 2 * 1024 * 1024 || totalBytes + blob.size > 64 * 1024 * 1024) throw new Error('invalid_size');
        totalBytes += blob.size;
        const bytes = await readLogBlob(blob);
        const text = filterLogLevels(new TextDecoder().decode(bytes), levels);
        result.files = {};
        if (logTypes.includes('android')) {
          const filename = `device-${item.deviceId}/android-${capture.id}.txt`;
          zip.file(filename, text); result.files.android = filename;
        }
        if (logTypes.includes('suitestream')) {
          const appText = filterSuitestreamLogs(text);
          const filename = `device-${item.deviceId}/suitestream-${capture.id}.txt`;
          zip.file(filename, appText); result.files.suitestream = filename;
          result.suitestreamMatchingLines = appText ? appText.trimEnd().split('\n').length : 0;
          result.suitestreamFilter = 'known_app_tags';
        }
        result.file = result.files.android || result.files.suitestream;
      } catch (_) { result.status = 'download_failed'; }
    }
    results.push(result);
  }
  const manifest = { siteId, createdAt: new Date().toISOString(), minutes, logTypes, levels,
    complete: results.every((item) => item.status === 'succeeded'), devices: results };
  zip.file('manifest.json', JSON.stringify(manifest, null, 2));
  zip.file('README.txt', 'Suitestream device log package\n\nSee manifest.json for selected devices, results, relay IDs and capture limits.\n'
    + 'Each device contains its own previous selected time window at collection time. Devices are collected sequentially.\n'
    + 'Only logs still retained on Android are available. Captures are limited to 2 MiB / 20,000 lines per device.\n'
    + 'Severity filtering applies to package files; saved captures retain the full default stream.\n'
    + 'Suitestream app files extract known app log tags from that same stream; they may omit library/process messages.\n'
    + 'An empty app file means no matching messages remained in the selected capture and levels.\n'
    + 'The app\'s private persistent boot file and unforwarded WebView console messages are not collected by this extract.\n'
    + 'A partial package lists missing, failed or unfinished devices in the manifest.\n');
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  return isActive() ? { blob, manifest } : null;
}

// FileReader also supports browsers where Blob.arrayBuffer is unavailable.
function readLogBlob(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('log_download_unreadable'));
    reader.readAsArrayBuffer(blob);
  });
}
